// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import isEqual from 'lodash/isEqual'
import React from 'react'
import { Mutation } from 'react-apollo'
import { Trans } from 'react-i18next'
import { alphaSort } from '../../../common/utils'
import { getTotalWeight, getTotalWeightColor } from '../../../common/weight/weightHelpers'
import { i18n } from '../../index'
import VitrineProductWeightsRow from './VitrineProductWeightsRow'
import { upsertVitrineProductWeight, vitrineProductWeightsQuery } from './vitrineProductQueries'
import css from './vitrineProductTable.css'
import type { VitrineProductWithWeight } from './vitrineProductTypes'

type Props = {
  stationId: string
  timeSlot: string
  data: Array<VitrineProductWithWeight>
}
type State = {
  data: Array<VitrineProductWithWeight>
}

const updateCache = (cache: any, { data: { upsertVitrineProductWeight } }: any) => {
  const queryVariables = {
    stationId: upsertVitrineProductWeight.station.id,
  }
  const { saltyVitrineProductWeights, sweetVitrineProductWeights } = cache.readQuery({
    query: vitrineProductWeightsQuery,
    variables: queryVariables,
  })

  const updateCacheItem = (timeSlot: any, productType: string) => {
    if (productType !== upsertVitrineProductWeight.vitrineProduct.type) {
      return timeSlot
    }

    return {
      ...timeSlot,
      weights:
        Number(timeSlot.timeslot) !== Number(upsertVitrineProductWeight.timeslot)
          ? timeSlot.weights
          : timeSlot.weights.map((item) => {
              if (item.id === upsertVitrineProductWeight.vitrineProduct.id) {
                return { ...item, weight: upsertVitrineProductWeight.weight }
              }

              return item
            }),
    }
  }

  const updatedData = {
    saltyVitrineProductWeights: [...saltyVitrineProductWeights.map((timeSlot) => updateCacheItem(timeSlot, 'Salty'))],
    sweetVitrineProductWeights: [...sweetVitrineProductWeights.map((timeSlot) => updateCacheItem(timeSlot, 'Sweet'))],
  }
  cache.writeQuery({
    query: vitrineProductWeightsQuery,
    data: updatedData,
    variables: queryVariables,
  })
}

class VitrineProductWeightsTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      data: props.data,
    }
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { data } = this.props

    if (data && !isEqual(data, prevProps.data)) {
      this.setState({
        data: data,
      })
    }
  }

  onWeightChange = (item: VitrineProductWithWeight, newValue: number) => {
    const { data } = this.state
    this.setState({
      data: data.map((dataItem) => (dataItem.id !== item.id ? dataItem : { ...item, weight: Number(newValue) })),
    })
  }
  onSubmit = (onMutate: (...args: Array<any>) => any, item: VitrineProductWithWeight) => {
    const { stationId, timeSlot } = this.props
    onMutate({
      variables: {
        stationId,
        vitrineProductWeight: {
          timeslot: Number(timeSlot),
          weight: Number(item.weight),
          vitrineProductId: item.id,
        },
      },
      update: updateCache,
    })
  }
  sortProducts = (products) => {
    const sortedCommonProducts = alphaSort(
      'name',
      products.filter((p) => p.isPublic),
    )
    const sortedStationsProducts = alphaSort(
      'name',
      products.filter((p) => !p.isPublic),
    )
    return [...sortedCommonProducts, ...sortedStationsProducts]
  }

  render() {
    const { data } = this.state
    const totalWeight = getTotalWeight(data || [])
    return (
      <div className={css.component}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={css.tableHeading}>
                {i18n.t('admin.vitrineProducts.weights.label.product')}
              </TableCell>
              <TableCell className={css.tableHeading} padding='checkbox'>
                <Trans
                  i18nKey='admin.vitrineProducts.weights.label.weight'
                  values={{ totalWeight }}
                  components={{ totalWeight: <strong style={{ color: getTotalWeightColor(totalWeight) }} /> }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length ? (
              this.sortProducts(data).map((item, i) => (
                <Mutation key={i} mutation={upsertVitrineProductWeight}>
                  {(onMutate) => (
                    <VitrineProductWeightsRow
                      onChange={(value) => this.onWeightChange(item, value)}
                      onSubmit={(item) => this.onSubmit(onMutate, item)}
                      data={item}
                      key={i}
                    />
                  )}
                </Mutation>
              ))
            ) : (
              <TableRow>
                <TableCell cellPad={2}>
                  <p className={css.empty}>{i18n.t('admin.vitrineProducts.weights.noProducts')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    )
  }
}

export default VitrineProductWeightsTable
